import type { BrowsableDocument, DocumentGroupingMode, DocumentSortMode } from './useDocumentBrowserStore';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import {
  formatDateShort,
  formatDocumentTypeLabel,
  formatFileSize,
  formatLanguageLabel,
  formatVersionLabel,
  getDocumentDisplayTitle,
  getSourceKindLabel,
  getUploaderLabel,
} from './documentBrowserUtils';
import { getDocumentGroups, getSortedDocuments } from './documentBrowserCollections';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface DocumentTableProps {
  documents: BrowsableDocument[];
  loading: boolean;
  groupBy: DocumentGroupingMode;
  sortBy: DocumentSortMode;
  onSelectDocument: (document: BrowsableDocument) => void;
}

export function DocumentTable({ documents, loading, groupBy, sortBy, onSelectDocument }: DocumentTableProps) {
  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Keine Dokumente in diesem Workspace.
      </div>
    );
  }

  const sortedDocuments = getSortedDocuments(documents, sortBy);
  const groupedDocuments = groupBy === 'none' ? [] : getDocumentGroups(documents, groupBy, sortBy);

  return (
    groupBy === 'none' ? (
      <div className="min-w-0 overflow-x-auto rounded-md border">
        <DocumentTableContent documents={sortedDocuments} onSelectDocument={onSelectDocument} />
      </div>
    ) : (
      <div className="flex min-w-0 flex-col gap-4">
        {groupedDocuments.map((group) => (
          <section key={group.key} className="min-w-0 overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
            <div className="flex items-center gap-3 border-b border-border/70 bg-muted/35 px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">{group.label}</h3>
              <Badge variant="secondary" className="rounded-full px-2.5 py-0 text-[11px]">
                {group.documents.length}
              </Badge>
            </div>
            <div className="overflow-x-auto">
              <DocumentTableContent documents={group.documents} onSelectDocument={onSelectDocument} />
            </div>
          </section>
        ))}
      </div>
    )
  );
}

function DocumentTableContent({
  documents,
  onSelectDocument,
}: {
  documents: BrowsableDocument[];
  onSelectDocument: (document: BrowsableDocument) => void;
}) {
  return (
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b bg-muted/50">
          <th className="text-left px-4 py-3 font-medium">Titel</th>
          <th className="text-left px-4 py-3 font-medium">Status</th>
          <th className="text-left px-4 py-3 font-medium">Quelle</th>
          <th className="text-left px-4 py-3 font-medium">Version</th>
          <th className="text-left px-4 py-3 font-medium">Hochgeladen</th>
          <th className="text-right px-4 py-3 font-medium">Aktionen</th>
        </tr>
      </thead>
      <tbody>
        {documents.map((doc) => (
          <tr key={doc.id} className="border-b last:border-0 transition-colors hover:bg-muted/20">
            <td className="px-4 py-3">
              <div className="flex min-w-[18rem] max-w-[36rem] flex-col gap-1">
                <span className="truncate font-medium text-foreground" title={getDocumentDisplayTitle(doc)}>
                  {getDocumentDisplayTitle(doc)}
                </span>
                {doc.displayTitle && (
                  <span className="truncate text-xs text-muted-foreground" title={doc.fileName}>
                    {doc.fileName}
                  </span>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>{formatFileSize(doc.fileSizeBytes)}</span>
                  <span>{doc.contentType}</span>
                  <span>{getUploaderLabel(doc)}</span>
                  {doc.documentType && <span>{formatDocumentTypeLabel(doc.documentType)}</span>}
                  {doc.language && <span>{formatLanguageLabel(doc.language)}</span>}
                  {doc.machineLabel && <span>{doc.machineLabel}</span>}
                  {doc.machineManufacturer && <span>{doc.machineManufacturer}</span>}
                  {doc.machineCategory && <span>{doc.machineCategory}</span>}
                </div>
              </div>
            </td>
            <td className="px-4 py-3">
              <DocumentStatusBadge status={doc.status} />
            </td>
            <td className="px-4 py-3">
              <div className="flex flex-col gap-1">
                <span className="font-medium text-foreground">{doc.sourceLabel ?? 'Manueller Upload'}</span>
                <Badge variant="outline" className="w-fit">
                  {getSourceKindLabel(doc.sourceKind)}
                </Badge>
              </div>
            </td>
            <td className="px-4 py-3 text-muted-foreground">
              {formatVersionLabel(doc)}
            </td>
            <td className="px-4 py-3 text-muted-foreground">
              {formatDateShort(doc.createdAt)}
            </td>
            <td className="px-4 py-3">
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => onSelectDocument(doc)}>
                  Details
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
